// src/components/Dashboard/Dashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Dashboard.css";

type Doctor = {
  id: string;
  name: string;
  email?: string | null;
  specialties?: string | null;
  country?: string | null;
  state?: string | null;
  website?: string | null;
  status?: string | null;
  bio?: string | null;
  medical_license_number?: string | null;
  photo_url?: string | null;
  retired?: boolean | null;
  is_active?: boolean | number | string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com";

const PLACEHOLDER_LOCAL = `${process.env.PUBLIC_URL}/logo192.png`;

function getAdminToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

function cleanValue(v?: string | null) {
  const s = (v || "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "string") return "";
  return s;
}

function normalizeWebsite(url?: string | null) {
  const raw = cleanValue(url);
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

function isActiveDoctor(d: Doctor) {
  const retired = String(d.retired ?? "").toLowerCase();
  const active = String(d.is_active ?? "").toLowerCase();

  if (retired === "true" || retired === "1") return false;
  if (active === "false" || active === "0" || active === "inactive")
    return false;

  return true;
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = getAdminToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data: any = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    throw new Error(
      typeof data?.detail === "string"
        ? data.detail
        : typeof data?.message === "string"
          ? data.message
          : `Request failed: ${res.status}`,
    );
  }

  return data;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [docs, setDocs] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>("");

  const year = new Date().getFullYear();

  const fetchDoctors = async () => {
    try {
      setErr("");
      setLoading(true);

      const data = await apiFetch("/admin/doctors/roster");
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.doctors)
          ? data.doctors
          : Array.isArray(data)
            ? data
            : [];

      setDocs(
        list.map((d: any) => ({
          id: String(d.id ?? d.doctor_id ?? ""),
          name: String(d.name ?? d.full_name ?? "Advisor").trim(),
          email: d.email ?? null,
          specialties: d.specialties ?? d.medical_field ?? null,
          country: d.country ?? null,
          state: d.state ?? null,
          website: d.website ?? null,
          status: d.status ?? null,
          bio: d.bio ?? null,
          medical_license_number: d.medical_license_number ?? null,
          photo_url: d.photo_url ?? null,
          retired: d.retired ?? null,
          is_active: d.is_active ?? null,
          created_at: d.created_at ?? null,
          updated_at: d.updated_at ?? null,
        })),
      );
    } catch (e: any) {
      setErr(e?.message || "Unable to load doctors.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const flag = sessionStorage.getItem("admin_doctors_refresh");
    if (flag) {
      sessionStorage.removeItem("admin_doctors_refresh");
      fetchDoctors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pending = useMemo(() => [], []);

  const approved = useMemo(() => docs.filter((d) => isActiveDoctor(d)), [docs]);

  const handleDelete = async (id: string) => {
    window.alert(
      "Delete should be handled from Doctor Roster. Use Deactivate if you want to keep the advisor record.",
    );
  };

  const goEdit = (id: string) => {
    navigate(`/edit-doctor/${id}`, {
      state: { backTo: "/dashboard" },
    });
  };

  const goView = (doctor: Doctor) => {
    navigate(`/admin/doctors/${doctor.id}`, {
      state: { doctor },
    });
  };

  return (
    <div className="dash-page">
      <div className="dash-header">
        <h1 className="dash-title">Advisor Profiles</h1>
        <div className="dash-tagline">
          AI Wellness Advisor with a human touch.
        </div>
      </div>

      {err ? <div className="dash-notice error">{String(err)}</div> : null}
      {loading ? <div className="dash-loading">Loading...</div> : null}

      <div className="dash-section">
        <h3 className="dash-section-title">
          Pending Applications ({pending.length})
        </h3>

        <div className="dash-muted">No pending advisor applications.</div>
      </div>

      <div className="dash-section">
        <h3 className="dash-section-title">
          Approved Advisors ({approved.length})
        </h3>

        <div className="dash-grid">
          {approved.map((d) => {
            const bio = (d.bio || "").trim();
            const shortBio = bio.length > 140 ? `${bio.slice(0, 140)}…` : bio;
            const website = normalizeWebsite(d.website);

            return (
              <div key={d.id} className="doc-card">
                <div className="doc-card-photo">
                  <img
                    src={cleanValue(d.photo_url) || PLACEHOLDER_LOCAL}
                    alt={d.name}
                  />
                </div>

                <div className="doc-card-body">
                  <div className="doc-card-name">{d.name}</div>

                  <div className="muted">
                    <strong>Specialty:</strong>{" "}
                    {cleanValue(d.specialties) || "—"}
                  </div>

                  <div className="muted">
                    <strong>Location:</strong>{" "}
                    {[cleanValue(d.state), cleanValue(d.country)]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>

                  <div className="muted">
                    <strong>Status:</strong> {cleanValue(d.status) || "On Call"}
                  </div>

                  {shortBio ? (
                    <div className="dash-bio">
                      <div className="dash-bio-title">Bio</div>
                      <div className="dash-bio-text">{shortBio}</div>
                    </div>
                  ) : null}

                  <div className="doc-card-footer">
                    <div className="dash-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => goEdit(d.id)}
                      >
                        Edit
                      </button>

                      <button
                        className="btn-secondary"
                        onClick={() => goView(d)}
                      >
                        View
                      </button>

                      <button
                        className="btn-danger"
                        onClick={() => handleDelete(d.id)}
                      >
                        Delete
                      </button>
                    </div>

                    <div className="dash-website-row">
                      {website ? (
                        <a
                          className="dash-website"
                          href={website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Visit Website
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="dash-refresh">
          <button
            className="btn-secondary"
            type="button"
            onClick={fetchDoctors}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="dash-footer">
        © {year} Doctor Is In App. All rights reserved.
      </div>
    </div>
  );
};

export default Dashboard;
